import { teacherMessage, activityStatus } from './teacher-language.js';
import type { ComponentChildren } from 'preact';
/**
 * TIP Shell — Preact view (Paper & Seal). PURE presentation over the store; it
 * owns no security logic. Trusted-chrome identity slots and the input box live
 * HERE, in the parent DOM, never in the frame.
 *
 * Design (DESIGN.md): warm paper, ink type, the serif "record voice" for
 * on-the-record sentences only, seal-red reserved for the moments that count.
 * The breach banner is deliberately the ONE place red floods the surface.
 */
import { useState } from 'preact/hooks';
import { useSyncExternalStore } from 'preact/compat';
import { getState, subscribe, stripChips, type LogEntry, type SlotEntry, type LeaderboardState } from './store.js';
import { registerFrameMount } from './dom.js';
import { clearTeacherDrafts } from './teacher-drafts.js';
import type { ServerClient, LibraryFeedbackBinding } from './api.js';
import { StudentReleaseFeedback } from './library-feedback.js';

/** Where a signed-out page lands: the Shell's own root, with the reason the gate reads. */
export function signInPage(reason: 'out' | 'expired'): string {
  return `${location.origin}${location.pathname}?signin=${reason}`;
}

const ROLE_LABEL: Record<string, string> = {
  teacher: 'a teacher', admin: 'a district admin', student: 'a student',
};

/**
 * WHO IS SIGNED IN, AND THE WAY OUT — on every signed-in screen (2026-09-30).
 *
 * There was no sign-out anywhere, though the server has revoked sessions properly since
 * migration 0014. On a shared classroom Chromebook that is the difference between a
 * student ending their session and the next student opening the first one's work.
 *
 * Only the ROLE is shown, not a name: the Shell holds no name for the viewer (identity
 * stays server-side), and the connection card already shows a teacher which Google
 * account their grades go out under.
 */
export function AccountControls({ api, role }: { api: ServerClient; role: string }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const signOut = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.logout();
      clearTeacherDrafts();
      location.assign(signInPage('out'));
    } catch {
      // Never pretend: the session was NOT revoked, so the teacher is still signed in.
      setBusy(false);
      setErr('TIP did not answer, so you are still signed in. Try again.');
    }
  };
  return (
    <span class="account">
      <span class="who">Signed in as {ROLE_LABEL[role] ?? role}</span>
      <button type="button" class="step account-out" disabled={busy} onClick={() => void signOut()}>
        {busy ? 'Signing out…' : 'Sign out'}
      </button>
      {err ? <span class="account-err" role="alert">{err}</span> : null}
    </span>
  );
}

/** The foot of every signed-in screen. Only links to pages that already exist and were approved. */
export function SiteFooter({ api }: { api: ServerClient }) {
  return (
    <footer class="site-foot">
      <a href={api.privacyUrl()} target="_blank" rel="noopener">Privacy policy</a>
    </footer>
  );
}

/** The notary-circle TIP monogram. Pure CSS ring; no external asset. */
export function SealMark({ large }: { large?: boolean }) {
  return <span class={`seal-mark${large ? ' lg' : ''}`} aria-hidden="true">TIP</span>;
}

/**
 * Capability slug → plain-English phrase (fixed vocabulary; unknown slugs humanize).
 * ONE map for the whole trusted chrome: the District Console's grant sentence and the
 * Teacher Console's "what this app can do" line must never describe the same capability
 * two different ways — a district reads both.
 */
export const CAP_PHRASES: Record<string, string> = {
  write_score: 'writes scores',
  read_scores: 'reads scores',
  display_names: 'displays first names',
  roster_as_data: 'can read the class list',
  runtime_ai: 'uses AI while students work',
  cross_section: 'can read information from more than one class',
  multi_student_scale: 'supports several students using it',
};

export function App({api, feedback}: {api?: ServerClient; feedback?: LibraryFeedbackBinding; children?: ComponentChildren} = {}) {
  // A fast session response can arrive before a passive effect subscribes.
  // Keep the snapshot and subscription together so sign-in never stays initializing.
  const s = useSyncExternalStore(subscribe, getState);
  const isResourceView = /^\/r\/[0-9a-f]{8}-[0-9a-f-]{27}\/?$/i.test(location.pathname);

  if (s.needLogin) {
    // Fail-closed sign-in gate. The button MUST be a TOP-LEVEL navigation
    // (location.assign) so Google's redirect + first-party Set-Cookie happen at
    // the top frame — never an iframe/fetch (the session cookie would not stick).
    return (
      <div class="gate">
        <SealMark large />
        <h1>Teacher Innovation Platform</h1>
        <p class="invocation record-voice i">
          Create classroom activities that fit your lessons. Start with a ready-made
          app or describe an idea of your own.
        </p>
        {s.loginMessage ? (
          <div class={`denied-note${s.loginTone === 'info' ? ' info' : ''}`} role="status">{s.loginMessage}</div>
        ) : null}
        <button
          type="button"
          class="act"
          disabled={!s.loginUrl}
          onClick={() => {
            if (s.loginUrl) location.assign(s.loginUrl);
          }}
        >
          Sign in with Google
        </button>
        <p class="foot record-voice i" style="font-size:15px;">
          Your school controls how activities handle student data.
        </p>
        {s.loginUrl ? (
          <p class="gate-links">
            <a href={s.loginUrl.replace(/\/auth\/login$/, '/privacy')} target="_blank" rel="noopener">
              Privacy policy
            </a>
          </p>
        ) : null}
      </div>
    );
  }

  if (s.denied) {
    if (isResourceView) return <div class="gate"><SealMark large /><h1>Resource unavailable</h1>
      <p class="invocation">This link is not available for your class right now. Ask your teacher to check the link and class access.</p>
      <a class="step" href={s.role==='student'?'/':'/?view=teach&workspace=resources'}>{s.role==='student'?'Back to Today':'Back to Resources'}</a></div>;
    return (
      <div class="gate">
        <SealMark large />
        <h1>Activity unavailable</h1>
        <p class="invocation">
          TIP could not open this activity. Check that you chose the right class and activity.
          If it still will not open, ask your school’s TIP administrator for help.
        </p>
        <div class="denied-note">{teacherMessage(s.denyReason, 'This activity may be blocked or unavailable for your class.')}</div>
      </div>
    );
  }

  const params = new URLSearchParams(location.search);
  const isAppView = params.get('view') === 'app';
  const isStudent = s.role === 'student'; // presentation only — never authz

  // App-first: the game IS the screen. ALL trusted chrome lives in ONE bar of FIXED
  // height (48px, never wraps): the seal mark, the seal note, the painted name chip, the
  // leaderboard popover and the way back — every trust signal OUTSIDE the frame.
  //
  // ⚠ NAME-LENGTH SIDE CHANNEL — read before changing this layout. The frame can read its
  // own innerWidth/innerHeight (and get `resize` events). If ANY name-bearing chrome could
  // change the frame's size, a frame could measure a name: the first version of this bar
  // wrapped and the frame was flex:1, so calling renderName on many slots and watching
  // innerHeight fingerprinted the width of a classmate's name (adversarial review P0,
  // 2026-09-29). So: the frame's size comes from CSS constants and the viewport ONLY
  // (index.html `.app-view iframe#sealed`); name chips live in `.app-bar-mid`, which is
  // clipped and can never grow the bar; chips are fixed-width, ellipsized, one per tag,
  // capped; the leaderboard popover and the breach banner are position:fixed, so they
  // cover the frame rather than move or resize it; the student page cannot scroll, and the
  // scrollbar gutter is reserved, so no trusted-chrome content can toggle a scrollbar
  // (review round 2: a long-named leaderboard did, and cost the frame 15px).
  // shell/name-leak.browser.test.ts pins all of this with classic scrollbars ON. No chrome
  // input at all (see the note below the frame). Status + diagnostics are teacher/dev only,
  // below the fold.
  return (
    <div class={`app-root${isStudent ? ' student' : ''}`}>
      <div class="app-view">
        <header class="app-bar">
          <SealMark />
          {/* Everything whose width varies lives here, clipped: it cannot grow the bar. */}
          <div class="app-bar-mid">
            <span class="wordmark">TIP</span>
            <span class="crumb">{s.appId ?? 'app'}</span>
            <span class="seal-note">
              <span class="dot seal" />
              {s.tier === null
                ? 'Activity protected by TIP'
                : s.appSeesFirstNames
                  ? 'TIP shares first names with this app'
                  : "TIP does not share names with this app"}
            </span>
            <SlotList slots={s.slots} selfTag={s.selfTag} />
            {!isStudent && s.tier ? (
              <span class="who">School privacy settings apply</span>
            ) : null}
          </div>
          {isStudent && !s.breach && api && feedback ? <StudentReleaseFeedback key={feedback.mountId} api={api} binding={feedback} /> : null}
          {s.leaderboard ? <LeaderboardPopover board={s.leaderboard} /> : null}
          {isStudent ? (
            <button
              type="button"
              class="quiet"
              onClick={() => {
                const u = new URL(location.href);
                u.pathname = '/';
                u.searchParams.delete('app');
                u.searchParams.delete('assignment');
                u.searchParams.delete('play');
                location.assign(u.toString());
              }}
            >
              ← Today
            </button>
          ) : null}
          {(isAppView || isResourceView) && (s.role === 'teacher' || s.role === 'admin') ? (
            // Teachers arrive here via the console's "open the student view";
            // give them the way back. A student who crafts ?view=app gets the same app
            // view WITHOUT this button (presentation only — the console is role-gated
            // server-side; the button just has no business in a student's bar).
            <button
              type="button"
              class="quiet"
              onClick={() => {
                const u = new URL(location.href);
                u.searchParams.delete('view');
                u.searchParams.delete('play');
                location.assign(u.toString());
              }}
            >
              {isResourceView ? '← Resources' : '← Teacher Console'}
            </button>
          ) : null}
        </header>

        {s.breach ? <BreachBanner reason={s.breach.reason} killed={s.breach.killed} /> : null}

        <div class="frame-sheet">
          <FrameMount />
        </div>

        {/* NO chrome (Shell-owned) input here (adversarial review): at high_assurance
            Bridge.forwardInput drops chrome keystrokes, so a visible Shell input would be
            a dead affordance. That is the ONLY tier-gated input path, and nothing uses it.
            Apps at BOTH tiers own their input in-frame: an app's own text box takes typed
            answers at high_assurance exactly as at standard (verified 2026-10-01, real
            browser: apps/starter-pack/high-assurance.browser.test.ts). The tier changes
            names only. The Rev 0.3-B design (input.keys grant, focus policing, derived-
            signal grammar) would make high_assurance input-blind; it was never built, and
            building it as specified would break typed answers in every name-blind school
            unless the grammar ships with it. */}
      </div>

      {!isStudent ? (
        <div class="app-diag">
          <div class="status-line">{activityStatus(s.status)}</div>
          <details class="diag">
            <summary>Details for technical support</summary>
            <p>This record helps support investigate a problem. You do not need to understand the codes.</p>
            <BridgeLog log={s.log} />
          </details>
        </div>
      ) : null}
    </div>
  );
}

function BreachBanner({ reason, killed }: { reason: string; killed: boolean }) {
  return (
    <div class="breach" role="alert">
      <strong>⛔ Activity stopped for a safety check.</strong>{' '}
      {killed ? 'This activity is blocked across your district. Your TIP administrator can review what happened.' : 'TIP is trying to block this activity across your district…'}
    </div>
  );
}

function FrameMount() {
  const ref = (el: HTMLDivElement | null): void => {
    if (el) registerFrameMount(el);
  };
  return <div id="frame-mount" ref={ref} />;
}

/** Trusted-chrome identity slots as strip chips. Names live ONLY in the parent DOM (JSX
 *  text children — Preact escapes; never innerHTML). Each chip is FIXED-width and
 *  ellipsized (index.html `.slot-chip`); which chips show, and in what order, is
 *  `stripChips` — the viewer's own always first. */
function SlotList({ slots, selfTag }: { slots: SlotEntry[]; selfTag: string | null }) {
  const chips = stripChips(slots, selfTag);
  return (
    <ul id="trusted-chrome-slots" class="slot-chips">
      {chips.map((sl) => (
        <li key={sl.tag} class="slot-chip" title="Shown by TIP. This name was not sent to the activity.">
          <span class="name">{sl.name}</span>
        </li>
      ))}
    </ul>
  );
}

/** Leaderboard as a strip popover. Broker-authoritative, server-ranked (desc by
 *  score). Names live ONLY here in the trusted parent DOM — they were never
 *  sent into the sealed frame; JSX text children only (no innerHTML), so a
 *  crafted users_ref.first_name cannot XSS the trusted shell. */
/** Leaderboard as a strip popover. Broker-authoritative, server-ranked (desc by
 *  score). Names live ONLY here in the trusted parent DOM — they were never
 *  sent into the sealed frame; JSX text children only (no innerHTML), so a
 *  crafted users_ref.first_name cannot XSS the trusted shell.
 *
 *  ⚠ FIXED OUTER BOX. The open popover sits OVER the frame, so its outline decides where
 *  the frame starts receiving the pointer: if the box grew with how names wrap (or with
 *  the row count), moving the mouse down past it told the frame the names' lengths
 *  (review round 3). So the box is a constant width and height (viewport-driven only),
 *  rows are fixed-height single lines that ellipsize, extra rows scroll INSIDE, and an
 *  empty board is the same box. Pinned by name-leak.browser.test.ts (LEADERBOARD). */
function LeaderboardPopover({ board }: { board: LeaderboardState }) {
  return (
    <details class="lb">
      <summary>Leaderboard</summary>
      <div class="lb-pop">
        <ol id="trusted-chrome-leaderboard">
          {board.rows.length === 0 ? (
            <li class="lb-empty"><span class="name">No scores yet.</span></li>
          ) : (
            board.rows.map((r) => (
              <li key={r.tag}>
                <span class="name">{r.name ?? `${r.tag.slice(0, 8)}…`}</span>
                <span class="score">{r.score}/{r.maxScore}</span>
              </li>
            ))
          )}
        </ol>
        <div class="lb-note">TIP puts scores in order from highest to lowest.</div>
      </div>
    </details>
  );
}

function BridgeLog({ log }: { log: LogEntry[] }) {
  return (
    <div id="bridge-log">
      {log.map((e) => (
        <div key={e.id} class={`row ${e.kind}`}>
          {e.t}&nbsp;&nbsp;{e.msg}
        </div>
      ))}
    </div>
  );
}
